import type { Arrayable } from '@vueuse/core';
import type { ImageryLayer, ImageryLayerCollection } from 'cesium';
import type { ComputedRef, MaybeRefOrGetter, Ref } from 'vue';
import type { MaybeRefOrAsyncGetter } from '../toPromiseValue';
import { computedAsync } from '@vueuse/core';
import { toValue, watchEffect } from 'vue';
import { toPromiseValue } from '../toPromiseValue';
import { useViewer } from '../useViewer';

export interface UseImageryLayerOptions {

  /**
   * The collection of ImageryLayer to be added
   * @default useViewer().value.imageryLayers
   */
  collection?: ImageryLayerCollection;

  /**
   * default value of `isActive`
   * @default true
   */
  isActive?: MaybeRefOrGetter<boolean>;

  /**
   * Ref passed to receive the updated of async evaluation
   */
  evaluating?: Ref<boolean>;

  /**
   * The second parameter passed to the `remove` function
   *
   * `imageryLayers.remove(layer,destroyOnRemove)`
   */
  destroyOnRemove?: MaybeRefOrGetter<boolean | undefined>;

  /**
   * The second parameter passed to the `add` function
   *
   * `imageryLayers.add(layer,index)`
   */
  index?: MaybeRefOrGetter<number | undefined>;
}

/**
 * Add `ImageryLayer` to the `ImageryLayerCollection`, automatically update when the data changes, and destroy the side effects caused by the previous `ImageryLayer`.
 *
 * Overload 1: Parameter supports passing in a single value.
 */
export function useImageryLayer<T extends ImageryLayer = ImageryLayer>(
  layer?: MaybeRefOrAsyncGetter<T | undefined>,
  options?: UseImageryLayerOptions,
): ComputedRef<T | undefined>;

/**
 * Add `ImageryLayer` to the `ImageryLayerCollection`, automatically update when the data changes, and destroy the side effects caused by the previous `ImageryLayer`.
 *
 * Overload 2: Parameter supports passing in an array.
 */
export function useImageryLayer<T extends ImageryLayer = ImageryLayer>(
  layers?: MaybeRefOrAsyncGetter<Array<T | undefined> | undefined>,
  options?: UseImageryLayerOptions,
): ComputedRef<T[] | undefined>;

export function useImageryLayer<T extends ImageryLayer>(
  data?: MaybeRefOrAsyncGetter<Arrayable<T | undefined>>,
  options: UseImageryLayerOptions = {},
) {
  const {
    destroyOnRemove,
    collection,
    isActive = true,
    evaluating,
    index,
  } = options;

  const result = computedAsync(
    () => toPromiseValue(data),
    [],
    {
      evaluating,
    },
  );

  const viewer = useViewer();

  watchEffect((onCleanup) => {
    const _isActive = toValue(isActive);
    if (_isActive) {
      const list = Array.isArray(result.value) ? [...result.value] : [result.value];
      const _collection = collection ?? viewer.value?.imageryLayers;
      const _index = toValue(index);
      if (collection?.isDestroyed()) {
        return;
      }
      list.forEach((item) => {
        if (!item) {
          return;
        }
        if (item?.isDestroyed()) {
          console.warn('ImageryLayer is destroyed');
          return;
        }
        _collection?.add(item, _index);
      });
      onCleanup(() => {
        const destroy = toValue(destroyOnRemove);
        list.forEach(item => item && _collection?.remove(item, destroy));
      });
    }
  });

  return result;
}
